import React, { useState } from 'react';
import { Share2, Copy, Check, X } from 'lucide-react';

/**
 * Universal share component.
 *  - Tries native navigator.share (iOS/Android share sheet) when available
 *  - Falls back to a custom drawer with WhatsApp / Telegram / X / Reddit / Copy
 *  - `variant="icon"` renders a compact icon button (for cards)
 *  - `variant="pill"` renders a labelled pill (for hero CTAs)
 */
const PLATFORMS = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    color: '#25D366',
    href: ({ url, title }) => `https://wa.me/?text=${encodeURIComponent(`${title} - ${url}`)}`,
  },
  {
    id: 'telegram',
    label: 'Telegram',
    color: '#229ED9',
    href: ({ url, title }) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    id: 'x',
    label: 'X / Twitter',
    color: '#FFFFFF',
    href: ({ url, title }) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    id: 'reddit',
    label: 'Reddit',
    color: '#FF4500',
    href: ({ url, title }) => `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    color: '#1877F2',
    href: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
];

const ShareButtons = ({ url, title = 'Outlaw Media', text = '', variant = 'icon', testId = 'share' }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const shareData = { title, text: text || title, url };
    if (navigator.share) {
      try { await navigator.share(shareData); return; }
      catch { /* user cancelled or unsupported - fall through to drawer */ }
    }
    setOpen(true);
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const btnClasses = variant === 'pill'
    ? 'inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-700 hover:border-red-500/50 text-xs font-black uppercase tracking-wider text-gray-200 hover:text-white transition-colors'
    : 'inline-flex items-center justify-center h-8 w-8 rounded-full bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-700 hover:border-red-500/50 text-gray-300 hover:text-white transition-colors';

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid={`${testId}-btn`}
        aria-label="Share"
        className={btnClasses}
      >
        <Share2 className="h-3.5 w-3.5" />
        {variant === 'pill' && <span>Share</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[55] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setOpen(false)}
          data-testid={`${testId}-drawer-backdrop`}
        >
          <div
            className="relative w-full max-w-sm bg-zinc-950 border border-red-900/50 rounded-t-2xl sm:rounded-2xl shadow-2xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testId}-drawer`}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute top-3 right-3 text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="p-5 sm:p-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-black mb-1">Share This</p>
              <h3 className="text-lg font-black text-white leading-tight mb-4 line-clamp-2">{title}</h3>

              <div className="grid grid-cols-3 gap-2 mb-4">
                {PLATFORMS.map((p) => (
                  <a
                    key={p.id}
                    href={p.href({ url, title })}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${testId}-platform-${p.id}`}
                    onClick={() => setOpen(false)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-red-500/40 transition-colors group"
                  >
                    <span
                      className="inline-flex items-center justify-center h-9 w-9 rounded-full text-black font-black text-sm"
                      style={{ background: p.color }}
                    >
                      {p.label[0]}
                    </span>
                    <span className="text-[10px] font-bold text-gray-300 group-hover:text-white uppercase tracking-wider">
                      {p.label}
                    </span>
                  </a>
                ))}
              </div>

              <button
                type="button"
                onClick={handleCopy}
                data-testid={`${testId}-copy-link`}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-black text-sm uppercase tracking-wider transition-colors"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Link Copied' : 'Copy Link'}
              </button>

              <p className="mt-3 text-[10px] text-gray-500 text-center break-all">{url}</p>
            </div>
            <style>{`
              @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
              .animate-slide-up { animation: slide-up 0.2s ease-out; }
            `}</style>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButtons;
